import { useMemo } from 'react';
import { READY_PCS_CONFIG } from '@components/common/ReadyPcsV2/constants';
import { ReadyPcsQuantityProps } from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';

export interface UseManageReadyPcsV2Params
  extends Partial<Pick<ReadyPcsQuantityProps, 'batch' | 'showOnlyTarget'>>,
    Pick<ReadyPcsQuantityProps, 'quantityProduced' | 'quantityTarget'> {}

export type UseManageReadyPcsV2Return = ReturnType<typeof useManageReadyPcsV2>;

const useManageReadyPcsV2 = ({ batch, showOnlyTarget, quantityProduced, quantityTarget }: UseManageReadyPcsV2Params) => {
  const targetQuantity = useMemo(() => {
    return quantityTarget ?? batch?.links?.length === 0 ? undefined : batch?.links?.reduce((acc, l) => acc + l.quantityTarget, 0);
  }, [batch?.links, quantityTarget]);

  const producedQuantity = quantityProduced ?? (showOnlyTarget ? targetQuantity : batch?.quantityReady);

  const shouldDisplayRightPart = targetQuantity != undefined && !showOnlyTarget;

  const actualQuantity = producedQuantity ?? 0;

  const targetQuantityExists = typeof targetQuantity === 'number';
  const amountOfBullets = targetQuantityExists ? Math.max(targetQuantity, actualQuantity) : actualQuantity;

  const { readyPcsBreakpoint, overlappedBulletColor, notFinishedBulletColor, finishedBulletColor } = READY_PCS_CONFIG;

  const getBulletBg = (index: number): string => {
    const hasOverlap = targetQuantityExists && index + 1 > targetQuantity;
    if (hasOverlap) return overlappedBulletColor;

    const isFinished = targetQuantityExists && index + 1 > actualQuantity;
    if (isFinished) return notFinishedBulletColor;

    return finishedBulletColor;
  };

  const shouldRenderBullets = amountOfBullets <= readyPcsBreakpoint;

  // Overlap can only happen when there's a target to exceed (shouldDisplayRightPart is true)
  const hasOverlap = shouldDisplayRightPart && targetQuantityExists && actualQuantity > targetQuantity;

  const progressFlex = hasOverlap ? targetQuantity : actualQuantity;
  const remainingFlex = targetQuantityExists ? (hasOverlap ? actualQuantity - targetQuantity : targetQuantity - actualQuantity) : 0;

  const timelineBackgroundColor = hasOverlap ? overlappedBulletColor : notFinishedBulletColor;

  const isCalculationComplete = batch != null;

  return {
    // Quantity values
    targetQuantity,
    producedQuantity,
    shouldDisplayRightPart,
    // Bullet values
    amountOfBullets,
    shouldRenderBullets,
    getBulletBg,
    progressFlex,
    remainingFlex,
    timelineBackgroundColor,
    finishedBulletColor,
    isCalculationComplete,
    hasOverlap,
    overlappedBulletColor,
  };
};

export { useManageReadyPcsV2 };
